import {
  apyToApr,
  calculateRateForCompoundType,
  TimeScale,
} from '../../../../utils/parsing'
import { CAction } from './types'

type VTokenMetadataLegacy = {
  vToken?: string
  exchangeRateCurrent?: bigint
  supplyRatePerBlock?: bigint
  borrowRatePerBlock?: bigint
  reserveFactorMantissa?: bigint
  totalBorrows?: bigint
  totalReserves?: bigint
  totalSupply?: bigint
  totalCash?: bigint
  isListed?: boolean
  collateralFactorMantissa?: bigint
  underlyingAssetAddress?: string
  vTokenDecimals?: bigint
  underlyingDecimals?: bigint
  venusSupplySpeed?: bigint
  venusBorrowSpeed?: bigint
  dailySupplyXvs?: bigint
  dailyBorrowXvs?: bigint
  pausedActions?: bigint
  liquidationThresholdMantissa?: bigint
  liquidationIncentiveMantissa?: bigint
  isBorrowAllowed?: boolean
  poolId?: bigint
}

type VTokenMetadataExtended = {
  vToken?: string
  exchangeRateCurrent?: bigint
  supplyRatePerBlockOrTimestamp?: bigint
  borrowRatePerBlockOrTimestamp?: bigint
  reserveFactorMantissa?: bigint
  supplyCaps?: bigint
  borrowCaps?: bigint
  totalBorrows?: bigint
  totalReserves?: bigint
  totalSupply?: bigint
  totalCash?: bigint
  isListed?: boolean
  collateralFactorMantissa?: bigint
  underlyingAssetAddress?: string
  vTokenDecimals?: bigint
  underlyingDecimals?: bigint
  pausedActions?: bigint
  borrowCap: number
  supplyCap: number
}

type AnyVTokenMetadata = Partial<VTokenMetadataLegacy & VTokenMetadataExtended>

/**
 * Safely extracts and normalizes all fields from a VTokenMetadata object.
 * Returns a plain JS object with stringified bigints (for display/logging).
 */
export function readVTokenMetadata(
  meta: AnyVTokenMetadata,
  token: any,
  unitSeconds?: number,
) {
  // Helper for safely selecting between possible field names
  const pick = <T>(...values: (T | undefined)[]): T | undefined =>
    values.find((v) => v !== undefined)
  return {
    vToken: meta.vToken ?? '',
    exchangeRateCurrent: meta.exchangeRateCurrent ?? 0n,
    supplyRatePerBlock:
      pick(meta.supplyRatePerBlock, meta.supplyRatePerBlockOrTimestamp) ?? 0n,
    borrowRatePerBlock:
      pick(meta.borrowRatePerBlock, meta.borrowRatePerBlockOrTimestamp) ?? 0n,
    reserveFactorMantissa: meta.reserveFactorMantissa ?? 0n,
    supplyCaps: meta.supplyCaps ?? 0n,
    borrowCaps: meta.borrowCaps ?? 0n,
    totalBorrows: meta.totalBorrows ?? 0n,
    totalReserves: meta.totalReserves ?? 0n,
    totalSupply: meta.totalSupply ?? 0n,
    totalCash: meta.totalCash ?? 0n,
    isListed: meta.isListed ?? true,
    collateralFactorMantissa: meta.collateralFactorMantissa ?? 0n,
    underlyingAssetAddress: meta.underlyingAssetAddress ?? '',
    cTokenDecimals: meta.vTokenDecimals ?? 0n,
    underlyingDecimals: meta.underlyingDecimals ?? 0n,
    venusSupplySpeed: meta.venusSupplySpeed ?? 0n,
    venusBorrowSpeed: meta.venusBorrowSpeed ?? 0n,
    dailySupplyXvs: meta.dailySupplyXvs ?? 0n,
    dailyBorrowXvs: meta.dailyBorrowXvs ?? 0n,
    // pausedActions: meta.pausedActions ?? 0n,
    liquidationThresholdMantissa: meta.liquidationThresholdMantissa ?? 0n,
    liquidationIncentiveMantissa: meta.liquidationIncentiveMantissa ?? 0n,
    isBorrowAllowed: meta.isBorrowAllowed ?? true,
    poolId: meta.poolId ?? 0n,
    pausedActions: decodePausedActions(meta.pausedActions ?? 0n),
    depositRate:
      apyToApr(
        calculateRateForCompoundType(
          pick(
            meta.supplyRatePerBlock,
            meta.supplyRatePerBlockOrTimestamp,
          )?.toString() ?? '0',
          token.chainId,
          TimeScale.BLOCK,
          unitSeconds,
        ) / 100,
      ) * 100,
    variableBorrowRate:
      apyToApr(
        calculateRateForCompoundType(
          pick(
            meta.borrowRatePerBlock,
            meta.borrowRatePerBlockOrTimestamp,
          )?.toString() ?? '0',
          token.chainId,
          TimeScale.BLOCK,
          unitSeconds,
        ) / 100,
      ) * 100,
    supplyCap: 0,
    borrowCap: 0,
  }
}

type ActionPausedMap = Record<CAction, boolean>

/**
 * Decodes a bigint bitmap returned from the contract into a map
 * mapping each Action enum member to whether it is paused.
 */
function decodePausedActions(bitmap: bigint): ActionPausedMap {
  const result = {} as ActionPausedMap

  for (let i = 0; i <= Object.keys(CAction).length / 2 - 1; i++) {
    // each bit corresponds to whether Action(i) is paused
    const isPaused = ((bitmap >> BigInt(i)) & 1n) === 1n
    result[i as CAction] = isPaused
  }

  return result
}

// // Example usage
// const bitmap = 0b10101n; // example bitmap (MINT, BORROW, SEIZE paused)
// const pausedMap = decodePausedActions(bitmap);

// console.log(pausedMap);
/*
{
  [Action.MINT]: true,
  [Action.REDEEM]: false,
  [Action.BORROW]: true,
  [Action.REPAY]: false,
  [Action.SEIZE]: true,
  ...
}
*/
